import { booleanAttribute, Component, input, OnChanges, OnInit, output } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { iValidationRule } from '@interfaces/dynamic-form-interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { Injectable } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
@Injectable()
export class DynamicDateAdapter extends NativeDateAdapter {
  public activeMask: string = 'dd/MM/yyyy';

  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.indexOf('-') > -1) {
      const str = value.split('T')[0].split('-');
      const year = Number(str[0]);
      const month = Number(str[1]) - 1;
      const day = Number(str[2]);
      return new Date(year, month, day);
    }
    return super.parse(value);
  }

  override format(date: Date, displayFormat: any): string {
    return this.formatDate(date, this.activeMask);
  }

  private formatDate(date: Date, mask: string): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const monthLong = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
    const monthShort = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);

    switch (mask) {
      case 'dd/MM/yyyy': return `${day}/${month}/${year}`;
      case 'dd/MM/yy': return `${day}/${month}/${String(year).slice(-2)}`;
      case 'dd/MM/yyyy HH:mm': return `${day}/${month}/${year} ${hours}:${minutes}`;
      case 'MM/dd/yyyy': return `${month}/${day}/${year}`;
      case 'yyyy-MMM-dd': return `${year}-${monthShort}-${day}`;
      case 'd MMMM, y': return `${date.getDate()} ${monthLong}, ${year}`;
      case "EEEE, dd 'de' MMMM 'de' yyyy":
        return `${weekday}, ${monthLong} ${day}, ${year}`;
      default: return `${day}/${month}/${year}`;
    }
  }
}
@Component({
  selector: 'app-generic-input',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgxMaskDirective,
    TranslateModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
  ],
  templateUrl: './generic-input.component.html',
  styleUrl: './generic-input.component.scss',
  providers: [
    provideNgxMask(),
    { provide: DateAdapter, useClass: DynamicDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' }
  ],
  host: {
    class: 'form-group'
  }
})

export class GenericInputComponent implements OnInit, OnChanges {
  type = input<string>('text');
  label = input<string>();
  value = input<string>();
  loading = input<boolean>(false);
  placeholder = input<string>('');
  control = input<any>();
  options = input<any[]>();
  handleChangeEvent = output<any>();
  handleBlurEvent = output<any>();
  max = input<any>();
  min = input<any>();
  maskType = input<'d MMMM, y' | 'MM/dd/yyyy' | 'yyyy-MMM-dd' | 'dd/MM/yyyy' | 'dd/MM/yy' | 'dd/MM/yyyy HH:mm' | "EEEE, dd 'de' MMMM 'de' yyyy">('d MMMM, y');
  readOnly = input(false, { transform: booleanAttribute });
  notAllowed = input(false, { transform: booleanAttribute });
  maxLength = input<number>(1000);
  minLength = input<number>(0);
  validations = input<iValidationRule[]>([]);

  constructor(private dateAdapter: DateAdapter<Date>) { }

  ngOnInit(): void {
    const ctrl = this.control();

    if (ctrl) {
      this.applyDocumentFormatting();

      if (this.type() === 'dateMask' && typeof ctrl.value === 'string' && ctrl.value.includes('-')) {
        const dateObj = this.stringToDate(ctrl.value);
        if (dateObj) {
          ctrl.setValue(dateObj, { emitEvent: false });
        }
      }

      if (this.type() === 'date' && typeof ctrl.value === 'string' && ctrl.value.includes('-')) {
        const formatted = this.formatDateForInput(ctrl.value);
        ctrl.setValue(formatted, { emitEvent: false });
      }

      ctrl.valueChanges.subscribe((val: any) => {
        if (this.type() === 'document') {
          if (typeof val === 'string' && val) {
            const raw = this.cleanDocumentValue(val);
            if (raw !== val) {
              ctrl.setValue(raw, { emitEvent: false });
            }
          }
          return;
        }

        if (this.type() === 'dateMask') {
          if (val instanceof Date) {
            const formatted = this.formatDateForInput(val);
            this.handleChangeEvent.emit(formatted);
            return;
          }

          if (typeof val === 'string' && val.includes('-')) {
            const dateObj = this.stringToDate(val);
            if (dateObj) {
              ctrl.setValue(dateObj, { emitEvent: false });
            }
            return;
          }
        }

        if (this.type() === 'date') {
          if (val instanceof Date) {
            const formatted = this.formatDateForInput(val);
            ctrl.setValue(formatted, { emitEvent: false });
          }
        }
      });
    }

    const adapter = this.dateAdapter as DynamicDateAdapter;
    adapter.activeMask = this.maskType();
  }

  ngOnChanges(): void {
    this.applyDocumentFormatting();
  }

  private applyDocumentFormatting(): void {
    const ctrl = this.control();
    if (!ctrl || this.type() !== 'document') return;

    if (!ctrl.hasValidator(this.documentFormatValidator)) {
      ctrl.addValidators(this.documentFormatValidator);
    }
    if (ctrl.value) {
      const raw = this.cleanDocumentValue(String(ctrl.value));
      if (raw !== ctrl.value) {
        ctrl.setValue(raw, { emitEvent: false });
      }
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  documentDisplayValue(): string {
    const value = this.control()?.value;
    if (!value) return '';
    return this.formatDocumentValue(String(value));
  }

  get errorMessage(): string {
    if (this.control().touched && this.control().value) {
      if (this.control()?.getError('minlength')) {
        const message = this.validations().find(validation => validation.type === 'minLength')?.message;
        if (message) {
          return message;
        }
      } else if (this.control()?.getError('maxlength')) {
        const message = this.validations().find(validation => validation.type === 'maxLength')?.message;
        if (message) {
          return message;
        }
      }
      else if (this.control()?.getError('pattern')) {
        const message = this.validations().find(validation => validation.type === 'regex')?.message;
        if (message) {
          return message;
        }
      }
      else if (this.control()?.getError('min')) {
        const message = this.validations().find(validation => validation.type === 'minValue')?.message;
        if (message) {
          return message;
        }
      }
      else if (this.control()?.getError('max')) {
        const message = this.validations().find(validation => validation.type === 'maxValue')?.message;
        if (message) {
          return message;
        }
      }
    }
    return '';
  }

  displayValue(): any {
    const val = this.control()?.value;
    if (!val) return '';
    const date = this.stringToDate(val);
    return date;
  }

  stringToDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (typeof value === 'string') {
      const datePart = value.split('T')[0];
      const parts = datePart.split(/[-/]/);

      if (parts.length === 3) {
        const isIso = parts[0].length === 4;
        const year = isIso ? parseInt(parts[0], 10) : parseInt(parts[2], 10);
        const month = isIso ? parseInt(parts[1], 10) - 1 : parseInt(parts[1], 10) - 1;
        const day = isIso ? parseInt(parts[2], 10) : parseInt(parts[0], 10);

        const d = new Date(year, month, day);
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }
    return new Date(value);
  }

  toDate(value: any): Date | null {
    return this.stringToDate(value);
  }

  handleDateChange(value: Date | null) {
    if (value) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}`;

      if (this.type() === 'dateMask') {
        this.control().setValue(value, { emitEvent: false });
        this.handleChangeEvent.emit(formatted);
      } else {
        this.control().setValue(formatted, { emitEvent: false });
        this.handleChangeEvent.emit(formatted);
      }
    } else {
      this.handleChangeEvent.emit('');
    }
  }

  formatDateForInput(value: any): string {
    if (!value) return '';
    let date: Date | null;
    if (value instanceof Date) {
      date = value;
    } else {
      date = this.stringToDate(value);
    }
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getvalidationMessage(type: string): string {
    const message = this.validations().find(validation => validation.type === type)?.message;
    return message ?? '';
  }

  handleChangeInput(event: any) {
    this.handleChangeEvent.emit(event.target.value);
  }

  checkNaturalNumber(event: KeyboardEvent) {
    if (this.type() === 'naturalNumber') {
      const charCode = event.charCode;
      if (charCode !== 0 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    }
  }

  handleBlurInput() {
    this.control()?.markAsTouched();
    this.handleBlurEvent.emit(null);
  }

  handleDocumentInput(event: any) {
    const input = event.target as HTMLInputElement;
    const cursorFromEnd = input.value.length - (input.selectionStart ?? input.value.length);
    const raw = this.cleanDocumentValue(input.value);
    const formatted = this.formatDocumentValue(input.value);

    const ctrl = this.control();
    if (ctrl && !ctrl.hasValidator(this.documentFormatValidator)) {
      ctrl.addValidators(this.documentFormatValidator);
    }
    ctrl?.setValue(raw);
    this.handleChangeEvent.emit(raw);

    input.value = formatted;
    const newPos = Math.max(0, formatted.length - cursorFromEnd);
    queueMicrotask(() => input.setSelectionRange(newPos, newPos));
  }

  private cleanDocumentValue(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 14);
  }

  private formatDocumentValue(value: string): string {
    const raw = this.cleanDocumentValue(value);
    const isCnpj = /[A-Z]/.test(raw) || raw.length > 11;
    return isCnpj ? this.formatCnpj(raw) : this.formatCpf(raw);
  }

  get isDocumentInvalid(): boolean {
    const raw = String(this.control()?.value ?? '').replace(/[^a-zA-Z0-9]/g, '');
    if (!raw) {
      return false;
    }
    const isCompleteCpf = /^\d{11}$/.test(raw);
    const isCompleteCnpj = raw.length === 14;
    return !(isCompleteCpf || isCompleteCnpj);
  }

  private documentFormatValidator = (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '').replace(/[^a-zA-Z0-9]/g, '');
    if (!raw) {
      return null;
    }
    const isCompleteCpf = /^\d{11}$/.test(raw);
    const isCompleteCnpj = raw.length === 14;
    return (isCompleteCpf || isCompleteCnpj) ? null : { mask: true };
  };

  private formatCpf(raw: string): string {
    const d = raw.slice(0, 11);
    if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
    return d;
  }

  private formatCnpj(raw: string): string {
    const d = raw.slice(0, 14);
    if (d.length > 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
    if (d.length > 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
    return d;
  }
}
